import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { RotateCcw, Sparkles } from "lucide-react";
import { formatAttribution } from "@/lib/text";

const QUALITY = [
  { q: 0, label: "Again", hint: "I blanked", cls: "border-destructive/60 text-destructive" },
  { q: 1, label: "Hard", hint: "needed a peek", cls: "border-foreground/40" },
  { q: 2, label: "Good", hint: "recalled it", cls: "border-accent/60 text-accent" },
  { q: 3, label: "Easy", hint: "cold", cls: "border-primary/60 text-primary" },
];

/** Map a verse's SM-2 state to a mastery fraction (0-1). */
function masteryOf(v) {
  const reps = v.repetitions || 0;
  const interval = v.interval_days || 0;
  if (reps === 0) return 0;
  if (interval >= 21) return 1;      // mature — light it up
  if (interval >= 7) return 0.66;    // reviewing
  if (interval >= 1) return 0.33;    // learning
  return 0.15;                        // seen once, not yet learned
}

/** Small circular ring showing SM-2 mastery for one verse. */
function MasteryRing({ verse, active }) {
  const pct = masteryOf(verse);
  const r = 14; const c = 2 * Math.PI * r;
  const dash = c * pct;
  return (
    <div className={`relative flex flex-col items-center gap-1 ${active ? "opacity-100" : ""}`}
      title={`Week ${verse.week_number} · ${Math.round(pct * 100)}% mastery`}>
      <svg width="34" height="34" viewBox="0 0 34 34" className="-rotate-90">
        <circle cx="17" cy="17" r={r} fill="none" stroke="hsl(var(--secondary))" strokeWidth="2" />
        <circle cx="17" cy="17" r={r} fill="none"
          stroke={pct >= 1 ? "hsl(var(--primary))" : "hsl(var(--accent))"}
          strokeWidth="2.5" strokeLinecap="round"
          strokeDasharray={`${dash} ${c}`}
          style={{ transition: "stroke-dasharray 0.5s ease" }} />
        <text x="17" y="17" textAnchor="middle" dominantBaseline="central"
          transform="rotate(90 17 17)"
          className={`font-serif text-[10px] tabular-nums ${active ? "fill-primary" : "fill-muted-foreground"}`}>
          {verse.week_number}
        </text>
      </svg>
    </div>
  );
}

export default function Verses() {
  const [verses, setVerses] = useState([]);
  const [mode, setMode] = useState("review"); // review | all
  const [idx, setIdx] = useState(0);
  const [showAnswer, setShowAnswer] = useState(false);

  const load = async () => {
    const { data } = await api.get("/verses");
    setVerses(data);
    setIdx(0);
    setShowAnswer(false);
  };
  useEffect(() => { load(); }, []);

  const due = useMemo(() => verses.filter((v) => v.due), [verses]);
  const deck = mode === "review" ? due : verses;
  const current = deck[idx];
  const doneCount = verses.length - due.length;

  const rate = async (q) => {
    if (!current) return;
    try {
      await api.post(`/verses/${current.week_number}/rate`, { quality: q });
      const nextIdx = idx + 1;
      if (nextIdx >= deck.length) {
        toast.success("You made it through the deck.");
        await load();
      } else {
        setIdx(nextIdx);
        setShowAnswer(false);
      }
    } catch {
      toast.error("Could not save that answer.");
    }
  };

  const reset = async () => {
    if (!window.confirm("Reset all memory verse progress?")) return;
    await api.post("/verses/reset");
    toast.success("Reset.");
    load();
  };

  return (
    <div className="max-w-3xl mx-auto">
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-primary">Memorize · Eleven verses</p>
          <h1 className="font-serif text-5xl mt-3 leading-tight">Hide it in your heart.</h1>
          <p className="text-muted-foreground mt-3 max-w-xl">
            One card at a time, spaced out so it actually sticks. Rate honestly — the app
            will bring the hard ones back sooner and the easy ones later.
          </p>
        </div>
        <button onClick={reset} data-testid="verses-reset" className="text-xs text-muted-foreground hover:text-destructive flex items-center gap-1">
          <RotateCcw className="w-3 h-3" /> Reset
        </button>
      </div>

      <div className="mt-10 flex items-center gap-6 text-xs uppercase tracking-widest">
        <button data-testid="verses-mode-review" onClick={() => { setMode("review"); setIdx(0); setShowAnswer(false); }}
          className={`pb-1 border-b ${mode === "review" ? "border-primary text-primary" : "border-transparent text-muted-foreground"}`}>
          Due today · {due.length}
        </button>
        <button data-testid="verses-mode-all" onClick={() => { setMode("all"); setIdx(0); setShowAnswer(false); }}
          className={`pb-1 border-b ${mode === "all" ? "border-primary text-primary" : "border-transparent text-muted-foreground"}`}>
          All verses · {verses.length}
        </button>
        <div className="ml-auto text-muted-foreground normal-case tracking-normal">
          {doneCount}/{verses.length} learned
        </div>
      </div>

      {/* Mastery ring row */}
      {verses.length > 0 && (
        <div className="mt-8 flex flex-wrap items-center gap-3" data-testid="mastery-row">
          {verses.map((v) => (
            <MasteryRing key={v.week_number} verse={v}
              active={current && current.week_number === v.week_number} />
          ))}
        </div>
      )}

      {!current ? (
        <div className="mt-16 border border-dashed border-border p-12 text-center">
          <Sparkles className="w-6 h-6 text-primary mx-auto" />
          <p className="mt-4 font-serif text-2xl">All caught up.</p>
          <p className="text-muted-foreground mt-2">Come back tomorrow — new cards will be due.</p>
        </div>
      ) : (
        <div className="mt-10">
          <div className="text-xs uppercase tracking-widest text-muted-foreground mb-3">
            Card {idx + 1} of {deck.length} · Week {current.week_number} · {current.movement}
          </div>
          <div className="border border-border bg-card p-10 lg:p-14 shadow-[0_8px_30px_rgba(42,42,38,0.06)] min-h-[280px] flex flex-col">
            <div className="text-xs uppercase tracking-widest text-primary">{current.reference}</div>
            <div className="mt-8 flex-1 flex items-center">
              {showAnswer ? (
                <div className="w-full">
                  <blockquote data-testid="verse-text" className="font-serif text-2xl italic leading-relaxed">
                    {current.text}
                  </blockquote>
                  <div data-testid="verse-attribution"
                    className="mt-5 text-right font-serif italic text-sm text-muted-foreground tracking-wide">
                    {formatAttribution(current.reference)}
                  </div>
                </div>
              ) : (
                <div className="text-muted-foreground italic">
                  Recall the verse. Tap "Show" when ready.
                </div>
              )}
            </div>
            {!showAnswer && (
              <div className="mt-8 flex justify-end">
                <Button onClick={() => setShowAnswer(true)} data-testid="verse-show" className="rounded-full">
                  Show verse
                </Button>
              </div>
            )}
          </div>

          {showAnswer && (
            <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="verse-rate-row">
              {QUALITY.map((r) => (
                <button key={r.q} onClick={() => rate(r.q)} data-testid={`verse-rate-${r.q}`}
                  className={`border p-4 text-left hover:bg-secondary/40 transition-colors ${r.cls}`}>
                  <div className="font-serif text-xl">{r.label}</div>
                  <div className="text-xs text-muted-foreground mt-1">{r.hint}</div>
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
